import type { Breadcrumb, Event as SentryEvent, ReplayFrameEvent } from '@sentry/angular';
import type { SentryLog } from '../models/error-log';

/**
 * Ported from the Operations app's SanitizeUtil (xd-xdispatchweb `src/app/utils/sanitize.util.ts`
 * at a7873e40d, XD-62264), so the two copies can be diffed. What the portal adds:
 * - every key ending in `token` (resetToken, mfaSessionToken, reAuthToken), the Web Push `auth`
 *   key and a whole `creditCard` object are credentials;
 * - a card-shaped object (one with a security code) loses its `number` and expiry, so a mistyped
 *   card number that fails the Luhn check is dropped too;
 * - the SSO exchange travels as `/v2/InetUserProfile/{token}`, and its token stops at a colon;
 * - the backend echoes an unknown reset token in `Invalid Token '…'`;
 * - `extra`, every `contexts` entry, the Referer header and breadcrumbs are scrubbed, and Replay
 *   frames and replay events have their own helpers;
 * - no lodash, and the code stays inside the es2017 lib (no matchAll, no trimStart);
 * - removeProps and copyAndRemoveBase64Data are not ported: UtilityService already strips base64.
 */

/** Where a card number sits inside a digit run, as character offsets. */
interface CardNumberSpan {
	start: number;
	end: number;
}

/** A plain JSON-able record, as a card object in a request body. */
type DataRecord = Record<string, unknown>;

/** The free-text and data fields of a breadcrumb, a Replay breadcrumb frame or a Replay span frame. */
interface ScrubbableFrame {
	description?: string;
	message?: string;
	data?: object;
}

/** A replay event (`type: 'replay_event'`): an event plus the URLs its recording visited. */
export type ReplayEventWithUrls = SentryEvent & { urls?: unknown };

export const UNSERIALIZABLE_REQUEST_BODY = '[unserializable request body]';

/**
 * Standalone utility class for sanitizing data in error logs and HTTP requests.
 * This class has no dependencies to avoid circular dependency issues.
 */
export class SanitizeUtil {
	/**
	 * Keys whose values are credentials, matched at any depth, case-insensitively: every `*Token`
	 * (the session token, resetToken, mfaSessionToken, reAuthToken), the Web Push subscription's
	 * `auth` secret, an `Authorization` header, every `*Password` (including verifyPassword), and
	 * card data: the CVV (sent as `securityCode`), card-number fields and the order's whole
	 * `creditCard` object. A card number under a generic key such as `number` is caught by its
	 * digits, or by sitting next to a security code (see credentialSafeReplacer).
	 * `allowPasswordChange` and `creditCardProcessingMethod` do not match: the pattern is anchored.
	 * Test keys with isCredentialKey, which also handles dotted attribute keys (`user.token`).
	 */
	static readonly CREDENTIAL_KEY =
		/^(.*token|auth|authorization|.*password|.*secretkey|.*accesskey|.*licensekey|.*securitycode|cvv2?|cvc2?|.*cardnumber|.*ccnumber|.*creditcard)$/i;

	/** `"<credential key>": "<string value>"` inside JSON-ish text, e.g. a login request body. */
	private static readonly CREDENTIAL_JSON_PAIR =
		/("(?:[^"]*\.)?(?:[^"]*token|auth|authorization|[^"]*password|[^"]*secretkey|[^"]*accesskey|[^"]*licensekey|[^"]*securitycode|cvv2?|cvc2?|[^"]*cardnumber|[^"]*ccnumber|[^"]*creditcard)"\s*:\s*)"(?:[^"\\]|\\.)*"/gi;

	/** A key that makes the object holding it card data: a security code travels only with a card. */
	private static readonly CARD_SECRET_KEY = /^(securitycode|cvv2?|cvc2?)$/i;

	/** The rest of a card next to its security code; under these generic names it is only a card there. */
	private static readonly CARD_SIBLING_KEY = /^(number|expmonth|expyear|expdate|expiration\w*)$/i;

	/** A run of 13 or more digits joined by single spaces or dashes; card numbers are looked for inside it. */
	private static readonly DIGIT_RUN = /(^|\D)(\d(?:[ -]?\d){12,})(?!\d)/g;

	/**
	 * How people write a card number: one unbroken group, groups of four with a shorter tail
	 * (4111 1111 1111 1111), Amex 4-6-5 or Diners 4-6-4. Any other grouping, such as a list of
	 * six-digit order ids, is never read as a card number however its digits add up.
	 */
	private static readonly CARD_GROUPING = /^(\d+|4(,4)+(,[1-4])?|4,6,[45])$/;

	/** A CVV written straight after a card number, which is redacted with it. */
	private static readonly TRAILING_CVV = /^[ -]\d{3,4}(?!\d)/;

	/**
	 * Issuer prefix and length of each card brand: Visa, Mastercard (51-55, 2221-2720), Amex,
	 * Discover, Diners, JCB, UnionPay. A Luhn-valid number outside them, such as a parcel barcode
	 * or tracking number, is not read as a card; an epoch-millisecond timestamp starts with 1 and
	 * matches none.
	 */
	private static readonly CARD_BRAND = new RegExp('^(?:' + [
		'4(?:\\d{12}|\\d{15}|\\d{18})',
		'(?:5[1-5]\\d{2}|2(?:2(?:2[1-9]|[3-9]\\d)|[3-6]\\d{2}|7(?:[01]\\d|20)))\\d{12}',
		'3[47]\\d{13}',
		'(?:6011|64[4-9]\\d|65\\d{2})\\d{12,15}',
		'30[0-5]\\d{11}',
		'36\\d{12,17}',
		'3[89]\\d{14,17}',
		'35(?:2[89]|[3-8]\\d)\\d{12,15}',
		'62\\d{14,17}',
	].join('|') + ')$');

	/** The value of an `Authorization: Token …` header quoted in text. */
	private static readonly AUTH_SCHEME_CREDENTIAL = /\b(Token|Bearer)\s+[A-Za-z0-9._~+\/=-]{16,}/g;

	/**
	 * The SSO exchange token travels in the URL path (`/v2/InetUserProfile/{token}` here,
	 * `/v2/Sso/XdUserProfile/{token}` in the Operations app), so it shows up in every error URL
	 * and message. The token stops at a colon or a quote, so Angular's `Http failure response for
	 * <url>: 400 Bad Request` keeps its shape.
	 */
	private static readonly SSO_PATH_TOKEN = /(\/(?:InetUserProfile|Sso\/XdUserProfile)\/)[^\/?#;:'"\s]+/gi;

	/** The emailed password-reset link carries its token as a router matrix param: `#/reset;userId=…;token=…`. */
	private static readonly MATRIX_PARAM_TOKEN = /((?:;|%3B)token(?:=|%3D))[^;\/?#&"\s]+/gi;

	/**
	 * The backend answers a reset with an unknown token with `Invalid Token '<the token>'`
	 * (AccountService.INVALID_PW_RESET_TOKEN, kept for legacy parity), so the token comes back in
	 * the error's detailedMsg.
	 */
	private static readonly RESET_TOKEN_ECHO = /(\bInvalid Token ')[^']*(')/gi;

	/**
	 * The SSO relay route `/sso-relay/:token`; the parameterised `:token` placeholder is left alone.
	 * LowerCaseUrlSerializer lowercases this path, so the token is matched in any case.
	 */
	private static readonly SSO_RELAY_TOKEN = /(\/sso-relay\/)(?!:)[^\/?#;"\s]+/gi;

	/**
	 * The URL token patterns without the `g` flag, for a yes/no test: `test()` on a global regex
	 * carries `lastIndex` over from the previous call.
	 */
	private static readonly URL_TOKEN_TESTS = [
		SanitizeUtil.MATRIX_PARAM_TOKEN,
		SanitizeUtil.SSO_RELAY_TOKEN,
		SanitizeUtil.SSO_PATH_TOKEN,
	].map((pattern) => new RegExp(pattern.source, pattern.flags.replace('g', '')));

	/**
	 * A JSON copy of `value` with every credential dropped, card-shaped objects cleaned, credential
	 * text redacted and Errors kept readable. Throws on a value JSON cannot hold (a cycle).
	 */
	static toSafeJson(value: unknown): unknown {
		const json = JSON.stringify(value, SanitizeUtil.credentialSafeReplacer);
		return json === undefined ? undefined : JSON.parse(json);
	}

	/**
	 * The request body HttpInterceptorService attaches to a failed response as `error.body`, with
	 * every credential gone and its shape kept: a JSON string body (sign-in, sign-up) stays a
	 * string, an object body stays an object. FormData and binary bodies have no fields JSON can
	 * reach and pass through. Never throws.
	 */
	static redactRequestBody(body: unknown): unknown {
		try {
			if (typeof body === 'string') {
				return SanitizeUtil.redactCredentialText(body);
			}
			if (body === null || typeof body !== 'object' || SanitizeUtil.isOpaqueBody(body)) {
				return body;
			}
			return SanitizeUtil.toSafeJson(body);
		} catch {
			return UNSERIALIZABLE_REQUEST_BODY;
		}
	}

	/**
	 * Redacts credentials embedded in free text: JSON request bodies, auth headers, SSO exchange
	 * URLs, reset links, a reset token the backend echoed. Text that is itself a JSON object or array is parsed and redacted by key
	 * instead, which also reaches non-string values and JSON nested inside its strings.
	 */
	static redactCredentialText(text: string): string {
		const json = SanitizeUtil.parseJsonContainer(text);
		if (json !== undefined) {
			return JSON.stringify(json, SanitizeUtil.credentialSafeReplacer);
		}
		return SanitizeUtil.redactCardNumbers(text)
			.replace(SanitizeUtil.CREDENTIAL_JSON_PAIR, '$1"[redacted]"')
			.replace(SanitizeUtil.AUTH_SCHEME_CREDENTIAL, '$1 [redacted]')
			.replace(SanitizeUtil.SSO_PATH_TOKEN, '$1[redacted]')
			.replace(SanitizeUtil.MATRIX_PARAM_TOKEN, '$1[redacted]')
			.replace(SanitizeUtil.SSO_RELAY_TOKEN, '$1[redacted]')
			.replace(SanitizeUtil.RESET_TOKEN_ECHO, '$1[redacted]$2');
	}

	/**
	 * Card numbers (PANs) in text, whatever key or sentence they sit in. A run of digits can hold
	 * more than the card ("4111 1111 1111 1111 737", "qty 32 4111 1111 1111 1111"), so the card
	 * is found inside the run rather than by testing the run whole.
	 */
	static redactCardNumbers(text: string): string {
		return text.replace(SanitizeUtil.DIGIT_RUN, (match: string, lead: string, run: string) => {
			const card = SanitizeUtil.findCardNumber(run);
			if (!card) {
				return match;
			}
			const before = SanitizeUtil.redactCardNumbers(run.slice(0, card.start));
			const after = SanitizeUtil.redactCardNumbers(run.slice(card.end)).replace(SanitizeUtil.TRAILING_CVV, '');
			return `${lead}${before}[redacted]${after}`;
		});
	}

	/** True for a key that names a credential; a dotted attribute key is judged by its last segment. */
	static isCredentialKey(key: string): boolean {
		return SanitizeUtil.CREDENTIAL_KEY.test(key.slice(key.lastIndexOf('.') + 1));
	}

	/** True when a URL holds a password-reset or SSO token (matrix param, relay route or exchange path). */
	static urlCarriesToken(url: string): boolean {
		return typeof url === 'string' && SanitizeUtil.URL_TOKEN_TESTS.some((pattern) => pattern.test(url));
	}

	/** A card number held as a JSON number becomes '[redacted]'; every other number is unchanged. */
	static redactCardNumber(value: number): number | string {
		return Number.isInteger(value) && SanitizeUtil.isCardNumber(String(value)) ? '[redacted]' : value;
	}

	/**
	 * JSON.stringify replacer that never lets a credential through and keeps an Error readable
	 * (name/message/stack are non-enumerable, so a bare Error serialises to {}).
	 *
	 * A credential key is dropped, not kept with a marker: Sentry's server-side scrubber wipes a
	 * whole log attribute whose text mentions password, token or auth, so a surviving
	 * `"password":"[redacted]"` would cost the rest of the error along with it.
	 *
	 * An object holding a security code is a card whatever its other keys are called, so its
	 * `number` and expiry go with it; JSON.stringify then walks the returned copy, which drops the
	 * security code itself.
	 */
	static credentialSafeReplacer(key: string, value: unknown): unknown {
		if (key && SanitizeUtil.isCredentialKey(key)) {
			return undefined;
		}
		if (value instanceof Error) {
			return { name: value.name, message: value.message, stack: value.stack };
		}
		if (typeof value === 'string') {
			return SanitizeUtil.redactCredentialText(value);
		}
		if (typeof value === 'number') {
			return SanitizeUtil.redactCardNumber(value);
		}
		if (SanitizeUtil.isCardShaped(value)) {
			return SanitizeUtil.withoutCardSiblings(value);
		}
		return value;
	}

	/**
	 * Sentry's `beforeSendLog`: the last line of defence for every log, including a direct
	 * Sentry.logger call that bypasses ErrorService. It runs before the SDK serialises attributes,
	 * so it sees the raw values. A log that cannot be scrubbed is dropped rather than sent as-is.
	 */
	static redactSentryLog(log: SentryLog): SentryLog | null {
		try {
			const attributes: Record<string, unknown> = JSON.parse(JSON.stringify(log.attributes ?? {}, SanitizeUtil.credentialSafeReplacer));
			return {
				...log,
				message: SanitizeUtil.redactCredentialText(String(log.message)),
				attributes,
			};
		} catch {
			return null;
		}
	}

	/**
	 * Sentry's `beforeSend` and `beforeSendTransaction`: scrubs every place an event carries a URL
	 * or free text. The page URL and Referer, TraceService's routing span names and their `url`
	 * attribute, `http.client` spans, navigation and XHR breadcrumbs, an HttpErrorResponse message,
	 * a non-Error throw serialised into `extra`, and any context can all hold a reset or SSO token
	 * or a request body. Mutates and returns the event; one that cannot be scrubbed is dropped.
	 */
	static redactSentryEvent<T extends SentryEvent>(event: T): T | null {
		try {
			const redact = SanitizeUtil.redactCredentialText;
			if (event.transaction) {
				event.transaction = redact(event.transaction);
			}
			if (event.message) {
				event.message = redact(event.message);
			}
			if (event.request?.url) {
				event.request.url = redact(event.request.url);
			}
			const headers = event.request?.headers;
			if (headers?.['Referer']) {
				headers['Referer'] = redact(headers['Referer']);
			}
			for (const exception of event.exception?.values ?? []) {
				if (exception.value) {
					exception.value = redact(exception.value);
				}
			}
			for (const breadcrumb of event.breadcrumbs ?? []) {
				SanitizeUtil.scrubFrame(breadcrumb);
			}
			for (const span of event.spans ?? []) {
				if (span.description) {
					span.description = redact(span.description);
				}
				if (span.data) {
					span.data = SanitizeUtil.redactRecord(span.data);
				}
			}
			if (event.extra) {
				event.extra = SanitizeUtil.redactRecord(event.extra);
			}
			if (event.contexts) {
				for (const name of Object.keys(event.contexts)) {
					const context = event.contexts[name];
					if (context) {
						event.contexts[name] = SanitizeUtil.redactRecord(context);
					}
				}
			}
			return event;
		} catch {
			return null;
		}
	}

	/**
	 * Sentry's `beforeBreadcrumb`: a breadcrumb is scrubbed before the SDK stores it and before
	 * Replay copies it into a recording. Navigation breadcrumbs hold reset and SSO URLs, XHR ones
	 * the SSO exchange URL, console ones whatever was logged. One that cannot be scrubbed is dropped.
	 */
	static redactBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb | null {
		try {
			SanitizeUtil.scrubFrame(breadcrumb);
			return breadcrumb;
		} catch {
			return null;
		}
	}

	/**
	 * Replay's `beforeAddRecordingEvent`, which sees the custom frames Replay records itself:
	 * breadcrumbs, and performance spans whose description and data hold request and navigation
	 * URLs (`resource.xhr`, `navigation.push` with its previous URL). A frame that cannot be
	 * scrubbed is not recorded.
	 */
	static redactReplayFrame(frame: ReplayFrameEvent): ReplayFrameEvent | null {
		try {
			const data = frame.data;
			if (data.tag === 'breadcrumb' || data.tag === 'performanceSpan') {
				SanitizeUtil.scrubFrame(data.payload);
			}
			return frame;
		} catch {
			return null;
		}
	}

	/**
	 * A replay event lists every URL the recording visited and carries the page URL at send time.
	 * Mutates and returns the event; one that cannot be scrubbed is dropped.
	 */
	static redactReplayEvent(event: ReplayEventWithUrls): ReplayEventWithUrls | null {
		try {
			if (Array.isArray(event.urls)) {
				event.urls = event.urls.map((url: unknown) => typeof url === 'string' ? SanitizeUtil.redactCredentialText(url) : url);
			}
			return SanitizeUtil.redactSentryEvent(event);
		} catch {
			return null;
		}
	}

	/** A JSON copy of `record` with credential keys and credential text redacted at any depth. */
	private static redactRecord<R extends object>(record: R): R {
		return JSON.parse(JSON.stringify(record, SanitizeUtil.credentialSafeReplacer));
	}

	/** Scrubs a breadcrumb or a Replay frame in place. */
	private static scrubFrame(frame: ScrubbableFrame): void {
		if (typeof frame.description === 'string') {
			frame.description = SanitizeUtil.redactCredentialText(frame.description);
		}
		if (typeof frame.message === 'string') {
			frame.message = SanitizeUtil.redactCredentialText(frame.message);
		}
		if (frame.data) {
			frame.data = SanitizeUtil.redactRecord(frame.data);
		}
	}

	private static isCardShaped(value: unknown): value is DataRecord {
		return typeof value === 'object' && value !== null && !Array.isArray(value)
			&& Object.keys(value).some((key) => SanitizeUtil.CARD_SECRET_KEY.test(key));
	}

	private static withoutCardSiblings(card: DataRecord): DataRecord {
		const copy: DataRecord = {};
		for (const key of Object.keys(card)) {
			if (!SanitizeUtil.CARD_SIBLING_KEY.test(key)) {
				copy[key] = card[key];
			}
		}
		return copy;
	}

	/** A body JSON cannot see into; serialising one gives {} however much it holds. */
	private static isOpaqueBody(body: object): boolean {
		return (typeof FormData !== 'undefined' && body instanceof FormData)
			|| (typeof Blob !== 'undefined' && body instanceof Blob)
			|| body instanceof ArrayBuffer;
	}

	/** The longest card number in a digit run, made of whole groups in a card grouping; null if none. */
	private static findCardNumber(run: string): CardNumberSpan | null {
		const groups: CardNumberSpan[] = [];
		const digitGroup = /\d+/g;
		let group: RegExpExecArray | null;
		while ((group = digitGroup.exec(run)) !== null) {
			groups.push({ start: group.index, end: group.index + group[0].length });
		}
		let best: CardNumberSpan | null = null;
		for (let first = 0; first < groups.length; first++) {
			let digits = '';
			const sizes: number[] = [];
			for (let last = first; last < groups.length; last++) {
				digits += run.slice(groups[last].start, groups[last].end);
				sizes.push(groups[last].end - groups[last].start);
				if (digits.length > 19) {
					break;
				}
				const span = { start: groups[first].start, end: groups[last].end };
				if (SanitizeUtil.CARD_GROUPING.test(sizes.join(',')) && SanitizeUtil.isCardNumber(digits)
					&& (!best || span.end - span.start > best.end - best.start)) {
					best = span;
				}
			}
		}
		return best;
	}

	/** A card brand's prefix and length (separators ignored) that passes the Luhn check every PAN carries. */
	private static isCardNumber(candidate: string): boolean {
		const digits = candidate.replace(/[ -]/g, '');
		if (!SanitizeUtil.CARD_BRAND.test(digits)) {
			return false;
		}
		let sum = 0;
		for (let i = 0; i < digits.length; i++) {
			let digit = Number(digits[digits.length - 1 - i]);
			if (i % 2 === 1) {
				digit *= 2;
				if (digit > 9) {
					digit -= 9;
				}
			}
			sum += digit;
		}
		return sum % 10 === 0;
	}

	private static parseJsonContainer(text: string): object | undefined {
		const start = text.replace(/^\s+/, '').charAt(0);
		if (start !== '{' && start !== '[') {
			return undefined;
		}
		try {
			const parsed: unknown = JSON.parse(text);
			return parsed !== null && typeof parsed === 'object' ? parsed : undefined;
		} catch {
			return undefined;
		}
	}
}
